import { useEffect } from 'react';
import { refreshLennyStatus, signOut } from '../lib/identity';
import { capture } from '../lib/analytics';

/**
 * Fired when the register endpoint returns 403 (authenticated but not a
 * subscriber). The CTA links out to the newsletter so they can sign up free,
 * then refresh the entitlement before registering.
 */
export function SubscribePrompt({
  open,
  identityName,
  onClose,
}: {
  open: boolean;
  identityName: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface p-7 shadow-cardHover"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-xl">
          📬
        </span>
        <h2 data-private className="mt-4 text-xl font-semibold tracking-tight">
          {identityName ? `Hi ${identityName.split(' ')[0]} — ` : ''}This is for newsletter subscribers
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          Workshops are available to free and paid subscribers of Lenny's Newsletter. If you just
          signed up, refresh your Lenny status to check again.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              refreshLennyStatus();
              onClose();
            }}
            className="btn-accent w-full"
          >
            Refresh Lenny subscription status
          </button>
          <a
            href="https://www.lennysnewsletter.com/subscribe"
            target="_blank"
            rel="noreferrer"
            onClick={() => capture('newsletter_signup_clicked')}
            className="btn-secondary w-full"
          >
            Sign up free for Lenny's Newsletter →
          </a>
          <button
            type="button"
            onClick={() => {
              signOut();
              onClose();
            }}
            className="btn-secondary w-full"
          >
            Sign in as someone else
          </button>
        </div>
      </div>
    </div>
  );
}
